import { Search } from 'lucide-react';
import type { ReactNode } from 'react';
import { cx } from '../lib/cx.js';
import { Kbd } from '../primitives/Kbd.js';
import './shell.css';

export interface TopBarProps {
  /** Palette trigger, usually <PaletteTrigger>. */
  search?: ReactNode;
  /** Office selector (<OfficeSwitcher>). */
  office?: ReactNode;
  /** PulseSync freshness (<FreshnessBadge>). */
  freshness?: ReactNode;
  /** Phone presence + call pop slot (telephony WP fills it). */
  phone?: ReactNode;
  /** Sensor / operatory status pill slot. */
  sensors?: ReactNode;
  notifications?: ReactNode;
  userMenu?: ReactNode;
  /** Page title or breadcrumbs on the left. */
  title?: ReactNode;
  className?: string;
}

/**
 * Top bar with fixed slots in a fixed order (BUILD-PLAN 3.3): title, office,
 * palette trigger, then freshness, phone, sensors, notifications and the user
 * menu on the right. Real backdrop blur is allowed here.
 */
export function TopBar({
  search,
  office,
  freshness,
  phone,
  sensors,
  notifications,
  userMenu,
  title,
  className,
}: TopBarProps) {
  return (
    <header className={cx('vel-topbar', className)}>
      <div className="vel-topbar__left">
        {office}
        {title ? <div className="vel-topbar__title">{title}</div> : null}
      </div>
      <div className="vel-topbar__center">{search}</div>
      <div className="vel-topbar__right">
        {freshness}
        {phone}
        {sensors}
        {notifications}
        {userMenu}
      </div>
    </header>
  );
}

export interface PaletteTriggerProps {
  onOpen: () => void;
  /** Shortcut label; pass ['Cmd', 'K'] on macOS. */
  shortcut?: readonly string[];
  placeholder?: string;
  className?: string;
}

/** "Search or jump to... Ctrl K" button that opens the command palette. */
export function PaletteTrigger({
  onOpen,
  shortcut = ['Ctrl', 'K'],
  placeholder = 'Search or jump to...',
  className,
}: PaletteTriggerProps) {
  return (
    <button
      type="button"
      className={cx('vel-palette-trigger', className)}
      onClick={onOpen}
      aria-keyshortcuts={shortcut.join('+')}
    >
      <Search size={14} aria-hidden />
      <span className="vel-palette-trigger__text">{placeholder}</span>
      <Kbd keys={shortcut} />
    </button>
  );
}
